import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  catalogueStatusSchema,
  validityRuleDefinitionInputSchema,
  type CatalogueStatus,
  type RuleSeverity,
  type ValidityRule,
} from 'fuel-benchmark-contracts';
import { useEffect, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Select, type SelectOption } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { Textarea } from '../../components/ui/Textarea';
import { serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { RULE_SEVERITIES, toDefinitionJson } from './condition-display';

export interface ValidityRuleSheetProps {
  isOpen: boolean;
  onClose: () => void;
  rule: ValidityRule | null;
}

/**
 * A rule's key and protocol are its identity, so this edits only what the rule *does*: its definition, what
 * breaking it costs, and whether it is applied at all. The JSON is validated against the very schema the API
 * validates with, so a malformed operator is caught before a request is made.
 */
export const ValidityRuleSheet = ({ isOpen, onClose, rule }: ValidityRuleSheetProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();

  const [definitionText, setDefinitionText] = useState('');
  const [severity, setSeverity] = useState<RuleSeverity>('WARN');
  const [status, setStatus] = useState<CatalogueStatus>('ACTIVE');
  const [definitionError, setDefinitionError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateRule = useMutation(orpc.config.validityRules.update.mutationOptions());

  useEffect(() => {
    if (!isOpen || !rule) {
      return;
    }

    setDefinitionText(toDefinitionJson(rule.definition));
    setSeverity(rule.severity);
    setStatus(rule.status);
    setDefinitionError(null);
    setSubmitError(null);
  }, [isOpen, rule]);

  const onSave = async (): Promise<void> => {
    if (!rule) {
      return;
    }

    setDefinitionError(null);
    setSubmitError(null);

    let parsedJson: unknown;

    try {
      parsedJson = JSON.parse(definitionText);
    } catch {
      setDefinitionError(t('conditions.rules.form.invalidJson'));

      return;
    }

    const definition = validityRuleDefinitionInputSchema.safeParse(parsedJson);

    if (!definition.success) {
      setDefinitionError(definition.error.issues.map((issue) => `${issue.path.join('.') || 'definition'}: ${issue.message}`).join(' · '));

      return;
    }

    try {
      await updateRule.mutateAsync({ id: rule.id, definition: definition.data, severity, status });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.config.validityRules.list.key() }),
        // A rule change does not touch existing runs until they are revalidated, but the coverage figures a
        // manager is looking at may already be stale from someone else's revalidation.
        queryClient.invalidateQueries({ queryKey: orpc.benchmarks.coverageMatrix.key() }),
      ]);
      onClose();
    } catch (error) {
      setSubmitError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  };

  const severityOptions: SelectOption[] = RULE_SEVERITIES.map((value) => ({ value, label: t(`enums.severity.${value}`) }));
  const statusOptions: SelectOption[] = catalogueStatusSchema.options.map((value) => ({ value, label: t(`enums.status.${value}`) }));

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={rule ? rule.ruleKey : t('conditions.rules.form.title')}
      description={t('conditions.rules.form.subtitle')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={updateRule.isPending}
            onClick={() => void onSave()}
          >
            {t('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField
          id="rule-protocol"
          label={t('conditions.rules.columns.protocol')}
        >
          <p className="text-sm text-muted-foreground">{rule ? `${rule.protocolCode} v${rule.protocolVersion}` : t('common.notSet')}</p>
        </FormField>

        <FormField
          id="rule-definition"
          label={t('conditions.rules.form.definition')}
          error={definitionError ?? undefined}
          hint={t('conditions.rules.form.definitionHint')}
        >
          <Textarea
            id="rule-definition"
            className="font-mono text-xs"
            rows={14}
            spellCheck={false}
            value={definitionText}
            hasError={definitionError !== null}
            onChange={(event) => setDefinitionText(event.target.value)}
          />
        </FormField>

        <FormField
          id="rule-severity"
          label={t('conditions.rules.columns.severity')}
          hint={t('conditions.rules.form.severityHint')}
        >
          <Select
            id="rule-severity"
            options={severityOptions}
            value={severity}
            onChange={(event) => setSeverity(event.target.value as RuleSeverity)}
          />
        </FormField>

        <FormField
          id="rule-status"
          label={t('conditions.rules.columns.status')}
          hint={t('conditions.rules.form.statusHint')}
        >
          <Select
            id="rule-status"
            options={statusOptions}
            value={status}
            onChange={(event) => setStatus(event.target.value as CatalogueStatus)}
          />
        </FormField>

        {submitError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {submitError}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
};
